import { Plus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { SPORTS, fmtHeight } from "@/constants/sports";

const HEIGHTS = Array.from({ length: 25 }, (_, i) => 60 + i);

const Chip = ({ children, onRemove, testId, accent }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border bg-[#131927] px-3 py-1 text-xs text-white"
    style={{ borderColor: `${accent}80` }}>
    {children}
    {onRemove && <button data-testid={`${testId}-remove`} onClick={onRemove} className="text-slate-400 transition-colors duration-200 hover:text-white"><X className="h-3 w-3" /></button>}
  </span>
);

const AddMenu = ({ options, onPick }) => options.length > 0 && (
  <Select value="" onValueChange={onPick}>
    <SelectTrigger data-testid="recipe-add-select" className="h-7 w-auto gap-1 rounded-full border-dashed border-slate-700 bg-transparent px-3 text-xs text-slate-300">
      <Plus className="h-3 w-3" /> Add
    </SelectTrigger>
    <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const HeightPick = ({ value, onChange, label, testId }) => (
  <Select value={value ? String(value) : "none"} onValueChange={(v) => onChange(v === "none" ? null : Number(v))}>
    <SelectTrigger data-testid={testId} className="h-7 w-auto gap-1 rounded-full border-slate-700 bg-transparent px-3 text-xs text-slate-300">
      {label} {value ? fmtHeight(value) : "any"}
    </SelectTrigger>
    <SelectContent className="max-h-72 border-slate-800 bg-[#131927]">
      <SelectItem value="none">Any</SelectItem>
      {HEIGHTS.map((h) => <SelectItem key={h} value={String(h)}>{fmtHeight(h)}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const RecipeChips = ({ recipe, catalog, onChange }) => {
  const c = catalog?.[recipe.sport];
  const accent = SPORTS[recipe.sport]?.accent || "#FF2D78";
  if (!c) return null;
  const label = (list, k) => list.find((x) => x.key === k)?.label || k;
  const drop = (field, v) => onChange({ ...recipe, [field]: recipe[field].filter((x) => x !== v) });
  const add = (v) => {
    const [field, val] = v.split("::");
    if (field === "position_group") onChange({ ...recipe, position_group: val });
    else onChange({ ...recipe, [field]: [...(recipe[field] || []), val] });
  };
  const has = (f, v) => (recipe[f] || []).includes(v);
  const options = [
    ...c.positions.filter((p) => !has("positions", p)).map((p) => [`positions::${p}`, `Position: ${p}`]),
    ...(recipe.position_group ? [] : c.position_groups.map((g) => [`position_group::${g}`, `Unit: ${g}`])),
    ...c.tags.filter((t) => !has("tags", t.key)).map((t) => [`tags::${t.key}`, `Tag: ${t.label}`]),
    ...c.stats.filter((t) => !has("stats", t.key)).map((t) => [`stats::${t.key}`, `Stat: ${t.label}`]),
    ...c.class_years.filter((y) => !has("class_years", y)).map((y) => [`class_years::${y}`, `Class: ${y}`]),
  ];
  return (
    <div data-testid="recipe-chips" className="flex flex-wrap items-center gap-2">
      <Chip testId="recipe-chip-sport" accent={accent}><span className="font-data uppercase tracking-wider" style={{ color: accent }}>{c.label}</span></Chip>
      {(recipe.positions || []).map((p) => <Chip key={p} testId={`recipe-chip-position-${p}`} accent={accent} onRemove={() => drop("positions", p)}>{p}</Chip>)}
      {recipe.position_group && <Chip testId="recipe-chip-group" accent={accent} onRemove={() => onChange({ ...recipe, position_group: null })}>{recipe.position_group}</Chip>}
      {(recipe.tags || []).map((k) => <Chip key={k} testId={`recipe-chip-tag-${k}`} accent={accent} onRemove={() => drop("tags", k)}>{label(c.tags, k)} tag</Chip>)}
      {(recipe.stats || []).map((k) => <Chip key={k} testId={`recipe-chip-stat-${k}`} accent={accent} onRemove={() => drop("stats", k)}>{label(c.stats, k)}</Chip>)}
      {(recipe.class_years || []).map((y) => <Chip key={y} testId={`recipe-chip-class-${y}`} accent={accent} onRemove={() => drop("class_years", y)}>{y}</Chip>)}
      <HeightPick label="Min ht" value={recipe.min_height} onChange={(v) => onChange({ ...recipe, min_height: v })} testId="recipe-min-height" />
      <HeightPick label="Max ht" value={recipe.max_height} onChange={(v) => onChange({ ...recipe, max_height: v })} testId="recipe-max-height" />
      <AddMenu options={options} onPick={add} />
    </div>
  );
};
